import { ClientSidebar } from '@ecosystem/newron-client-lib';
import { actions, containers, elements, feedback } from '@ecosystem/newron-design-system';
import { FilterTrayTabId as FilterTrayTabIdEnum } from '@ecosystem/newron-design-system/build/exports/containers';
import React, { useEffect, useState } from 'react';
import { useAnalyticsContext } from '../../../analytics/analytics.provider';
import { userClient } from '../../../client/user.client';
import { MAX_ALLOWED_CUSTOM_CLASSES } from '../../../common/selection.menu/constants';
import { MenuSeparatorComponent } from '../../../common/selection.menu/menu.separator';
import { Formatting } from '../../../data/formatting';
import { useCorePageState } from '../../../page/provider';
import { PageSettingsManager } from '../../../page/settings';
import {
  DataType,
  EventElementCategory,
  EventElementType,
  EventName,
  EventParams,
  FilterSections,
  FormatHint,
  Params,
  PreferenceName,
} from '../../../types';
import { CustomPriceClass, SavedCustomPriceClass, SharePermissionsBody } from '../../../types/content/custom.types';
import { CustomPriceClasses } from '.././price/customPriceClasses';
import { useSavePriceClasses } from '../../savePriceClasses.hook';
import { ShareCustomModal } from '../../shareCustomModal';
import { useSidebar } from '../useSideBar.hook';
import {
  adjustClassesAfterDeletion,
  formatPricesForParams,
  getPriceClassesFromSettings,
  getSharedUsers,
  shareToUser,
  updateLabelErrors,
  validateLabelInput,
} from '../../utils';
import { CustomSegmentType } from '../../types';
import { SavedCustomSegmentsCard } from '../savedCustomSegmentsCard';
import { SavedCustomSegmentsModal } from '../saveCustomSegmentsModal';
import { AddClassButton } from '../addClassButton';
import { getCurrentActiveComponentNameForCustomTray } from '../customSegmentsUtils';
import { isPriceClassApplicable } from './utils';
import { ContextManager } from '../../../context/manager';

const { Button, ButtonGroup, IconButton } = actions;
const { Icon } = elements;
const { FilterTrayTabId, Tray } = containers;
const { Badge } = feedback;

function EditTrayComponent({ sections, updateSettings, currency, quantityType }: ComponentProps) {
  const { setCustomizationTray, customizationTrayTabId, dsl } = useCorePageState();
  const currentActiveComponentName = getCurrentActiveComponentNameForCustomTray(dsl.sections);

  const componentSettings = PageSettingsManager.getInstance().getComponentSettings(currentActiveComponentName);

  const { expanded, hideSidebar, openSidebar } = useSidebar('custom-price-tiers');

  const [activeTab, setActiveTab] = useState<FilterTrayTabIdEnum>(customizationTrayTabId || FilterTrayTabId.Configure);

  useEffect(() => {
    setActiveTab(customizationTrayTabId || FilterTrayTabId.Configure);
  }, [customizationTrayTabId]);

  const [showSaveModal, setShowSaveModal] = useState(false);
  const { trackPageEvent } = useAnalyticsContext();
  const { savedPriceClasses, currentActive, savePriceClasses, setIsActive, deleteSavedPriceClasses } =
    useSavePriceClasses();

  // function used to get the currency and rounding value from the price filter
  const getDefaultPriceSettings = () => {
    const market = sections.find(def => def.label === 'Market');
    return market.ungrouped?.definitions.find(def => def.roundTo);
  };

  // create a default price class object, null represent infinite value
  const defaultPriceClass: CustomPriceClass = {
    label: '',
    min: 0,
    max: null,
    roundTo: getDefaultPriceSettings()?.roundTo,
    currency: currency?.toUpperCase(),
    quantityType: quantityType,
  };

  const [priceClasses, setPriceClasses] = useState<CustomPriceClass[]>(
    getPriceClassesFromSettings(componentSettings, defaultPriceClass)
  );
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [priceClassToShare, setPriceClassToShare] = useState<SavedCustomPriceClass>();

  //We will need to force the Custom Price Tray to be opened once its rendered.
  useEffect(() => {
    if (!expanded) {
      openSidebar(); // Avoid multiple state changes by checking `expanded`
    }
  }, [expanded, openSidebar]);

  useEffect(() => {
    if (currentActive !== '') {
      handleApply();
    }
  }, [currentActive]);

  const openSaveModal = () => {
    if (!arePriceClassesValid()) return;
    setShowSaveModal(true);
  };

  const handleSave = (name: string, desc: string) => {
    recordAnalyticEventDetails('Save Custom Price Classes', priceClasses);
    savePriceClasses(name, desc, priceClasses);
    handleCancelSave();
  };

  const handleCancelSave = () => {
    setShowSaveModal(false);
  };

  const handleApply = () => {
    const formattedPrices = formatPricesForParams(priceClasses);

    updateSettings({
      ...componentSettings,
      customPriceTiers: [formattedPrices],
    });
  };

  /**
   * Function to update relevant states and validate the price classes/label after on change is handled
   * @param priceClassesToUpdate the updated price classes
   * @param type the type of input to validate
   */
  const updatePriceClasses = (priceClassesToUpdate: CustomPriceClass[]) => {
    setPriceClasses(updateLabelErrors(priceClassesToUpdate));
  };

  /**
   * Function to handle price change
   * @param index the index of the price class
   * @returns
   */
  const handleOnPriceChange = (index: number) => (value: number) => {
    if (!value) return;
    const normalizedRoundTo = Number(defaultPriceClass.roundTo);
    if (value === normalizedRoundTo) {
      value = priceClasses[index].min + normalizedRoundTo;
    }
    const nextIndex = index + 1;
    const updatedPriceClass = { ...priceClasses[index] };
    let updatedNextMinPriceClass = { ...priceClasses[nextIndex] };
    const copyPriceClass = [...priceClasses];
    updatedPriceClass.max = value;
    //auto update next min value, current max value=next min value
    if (priceClasses.length > nextIndex) {
      updatedNextMinPriceClass = { ...priceClasses[nextIndex] };
      updatedNextMinPriceClass.min = value;
      copyPriceClass[nextIndex] = updatedNextMinPriceClass;
    }
    copyPriceClass[index] = updatedPriceClass;
    const roundedPriceClasses = getRoundedPriceClasses(copyPriceClass, index, defaultPriceClass);
    updatePriceClasses(roundedPriceClasses);
  };

  /**
   * Function to handle price change
   * @param index the index of the price class
   * @param copyPriceClass price classes array
   * @returns rounded updated price classes
   */
  const getRoundedPriceClasses = (copyPriceClass: CustomPriceClass[], index: number, defaultPriceClass) => {
    let postIndex = index + 1;
    const normalizedRoundTo = Number(defaultPriceClass.roundTo);
    if (copyPriceClass[index].max < copyPriceClass[index].min) {
      // Iterate through previous indices in a single loop
      while (index >= 0) {
        copyPriceClass[index].max = Number(copyPriceClass[index + 1].min.toFixed(2));
        if (copyPriceClass[index].max <= copyPriceClass[index].min) {
          copyPriceClass[index].min = Number((copyPriceClass[index].max - normalizedRoundTo).toFixed(2));
        }
        index--;
      }
    }
    while (postIndex <= copyPriceClass.length - 1) {
      copyPriceClass[postIndex].min = Number(copyPriceClass[postIndex - 1].max.toFixed(2));
      if (copyPriceClass[postIndex].max <= copyPriceClass[postIndex].min) {
        copyPriceClass[postIndex].max = Number((copyPriceClass[postIndex - 1].max + normalizedRoundTo).toFixed(2));
      }
      postIndex++;
    }
    return copyPriceClass;
  };

  /**
   * Function to handle label change
   * @param index the index of the price class
   * @returns
   */
  const handleOnLabelChange = (index: number) => (label: string) => {
    const updatedPriceClass = { ...priceClasses[index] };
    const copyPriceClass = [...priceClasses];
    updatedPriceClass.label = label;
    copyPriceClass[index] = updatedPriceClass;
    updatePriceClasses(copyPriceClass);
  };

  /**
   * Function to handle tab change
   * @param tabId the tab id
   */
  const onTabChange = (tabId: FilterTrayTabIdEnum) => {
    setActiveTab(tabId);
  };

  /**
   * Function to add price classes
   */
  const addPriceClass = () => {
    //get previous Max value
    const index = priceClasses.length - 1;
    const prevMax = priceClasses[index].min;
    const newMax = prevMax + Number(defaultPriceClass.roundTo);

    const newPriceTier: CustomPriceClass = {
      ...defaultPriceClass,
      max: Number(newMax.toFixed(2)),
      min: Number(prevMax.toFixed(2)),
      label: priceClasses[index].label,
    };
    const newMaxTier: CustomPriceClass = {
      ...defaultPriceClass,
      max: null,
      min: Number(newMax.toFixed(2)),
    };

    // remove the last element and add the new price tier and the new max tier
    let newPriceClasses = [...priceClasses].slice(0, -1);
    newPriceClasses.push(newPriceTier, newMaxTier);
    setPriceClasses(newPriceClasses);
  };

  /**
   * Function to delete a price class
   * @param index the index of the price class to be deleted
   */
  const deletePriceClass = (index: number) => {
    let priceClassesToUpdate: CustomPriceClass[] = adjustClassesAfterDeletion(index, [
      ...priceClasses,
    ]) as CustomPriceClass[];
    // remove the price class based on index
    priceClassesToUpdate = priceClassesToUpdate.filter((_, i) => i !== index);
    setPriceClasses(priceClassesToUpdate);
  };

  /**
   * Function to get the round to value and format it into a label
   * @returns round to label to display
   */
  const getRoundToLabel = () => {
    if (defaultPriceClass.roundTo) {
      const formatted = Formatting.format(defaultPriceClass.roundTo, {
        dataType: DataType.Integer,
        formatHint: FormatHint.Money,
        currency: defaultPriceClass.currency,
      });
      if (formatted) {
        return `Enter prices that can be divided by ${formatted}`;
      }
      return ``;
    }
  };

  /**
   * Function to render the custom price classes
   * @param index the index of the price class
   * @returns JSX.Element
   */
  const renderCustomPriceClasses = (index: number, labelError?: string) => {
    const prevMax = index > 0 ? priceClasses[index - 1].max : null; // max(X-1)
    const nextMax = index < priceClasses.length - 1 ? priceClasses[index + 1].max : null; // min(X+1)
    return (
      <CustomPriceClasses
        key={`price_${index}`}
        onPriceChange={handleOnPriceChange}
        onLabelChange={handleOnLabelChange}
        index={index}
        prevMax={prevMax}
        nextMax={nextMax}
        priceClasses={priceClasses}
        labelError={labelError}
      />
    );
  };

  const getSavedCustomSegmentsCardLabels = (priceClass: SavedCustomPriceClass) => {
    const labelArray = [priceClass.priceClasses[0]?.currency, priceClass.priceClasses[0]?.quantityType];

    return labelArray;
  };

  /**
   * Render the saved price class filter
   * @param priceClass - the saved price class to render
   * @param index - index of the saved price class within the savedPriceClasses array
   * @returns - the Saved Price Class Filter card
   */
  const renderSavedPriceClass = (priceClass: SavedCustomPriceClass, index: number) => {
    const isApplicable = isPriceClassApplicable(priceClass, defaultPriceClass);
    return (
      <SavedCustomSegmentsCard
        key={`saved-price-class-${priceClass.name}-${index}`}
        customSegmentType={CustomSegmentType.customPriceClass}
        savedCardLabels={getSavedCustomSegmentsCardLabels(priceClass)}
        savedData={priceClass}
        isActive={currentActive === priceClass.preferenceId}
        isApplicable={isApplicable}
        handleApply={onApplyFromSavedCustomCard}
        handleDelete={onDeleteFromSavedCustomCard}
        handleShare={onShareFromSavedCustomCard}
      />
    );
  };

  /**
   * Callback function to handle the when user click Apply in Saved Custom card
   * @param savedPriceClasses the saved price classes
   */
  const onApplyFromSavedCustomCard = (savedPriceClasses: SavedCustomPriceClass) => {
    recordAnalyticEventDetails('Apply Saved Custom Price Classes', savedPriceClasses.priceClasses);
    setPriceClasses(savedPriceClasses.priceClasses);
    setIsActive(savedPriceClasses.preferenceId);
  };

  const arePriceClassesValid = (): boolean => {
    const areLabelsValid = validateLabelInput(priceClasses);
    return areLabelsValid;
  };

  /**
   * Callback function to handle the event when user click Apply in Customized tab
   */
  const onApplyFromCustomizedTab = () => {
    if (!arePriceClassesValid()) return;
    recordAnalyticEventDetails('Apply Custom Price Classes', priceClasses);
    handleApply();
    setIsActive('');
  };

  const onShareFromSavedCustomCard = (savedPriceClasses: SavedCustomPriceClass) => {
    setIsShareModalOpen(true);
    setPriceClassToShare(savedPriceClasses);
  };

  /**
   * Render the Save Price Class Modal for when the Save button is clicked
   * @returns - the Save Price Class Modal
   */
  const renderSavePriceClassModal = () => {
    const existingPriceClassNames = savedPriceClasses.map(item => item.name);
    return (
      <SavedCustomSegmentsModal
        customSegmentType={CustomSegmentType.customPriceClass}
        isOpen={showSaveModal}
        existingName={existingPriceClassNames}
        onSave={handleSave}
        onClose={handleCancelSave}
      />
    );
  };

  /**
   * Callback function to handle the when user click Delete in Saved Custom card
   * @param savedPriceClass - the saved price classes to delete
   * @param userId - the userId of the user
   */
  const onDeleteFromSavedCustomCard = async (savedPriceClass: SavedCustomPriceClass, userId: string) => {
    recordAnalyticEventDetails('Delete Saved Custom Price Classes', null);
    await deleteSavedPriceClasses(savedPriceClass, userId);
    if (savedPriceClass.preferenceId === currentActive) {
      setIsActive('');
    }
  };

  /**
   * Function to record the analytic event details
   * @param event - the event that is being recorded
   * @param priceClassList - the list of price classes
   */
  const recordAnalyticEventDetails = (event: string, priceClassList: CustomPriceClass[] | null) => {
    let params: EventParams = {
      elementType: EventElementType.Button,
      elementCategory: EventElementCategory.CustomPriceFilter,
      elementName: event,
    };
    if (priceClassList) {
      const list = priceClassList.map(priceClass => priceClass.min.toString());
      params.customPriceClasses = list;
    }
    trackPageEvent(EventName.PageElementClicked, params);
  };

  const renderSharePriceClassModal = () => {
    return (
      <ShareCustomModal
        isOpen={isShareModalOpen}
        itemToShare={priceClassToShare}
        preferenceName={PreferenceName.segment_price}
        onClose={() => setIsShareModalOpen(false)}
        onSuccess={() => {
          setIsShareModalOpen(false);
        }}
        onShareError={() => {
          setIsShareModalOpen(false);
        }}
        getUsers={userClient.getSameAccessUsers}
        getSharedUsers={getSharedUsers}
        shareToUser={onShareToUser}
      />
    );
  };

  /**
   * Function to share the price classes to a user.
   * Called when the user clicks the "Apply changes" button in the share modal
   * @param preferenceId - the id of the preference to be shared
   * @param permissionsBody - the permissions body
   */
  const onShareToUser = async (preferenceId: string, permissionsBody: SharePermissionsBody[]) => {
    recordAnalyticEventDetails('Share Saved Custom Price Classes', null);
    await shareToUser(preferenceId, PreferenceName.segment_price, permissionsBody);
  };

  return (
    <ClientSidebar.Content>
      <Tray>
        <Tray.Header padding="medium">
          {expanded && <div className="flex-grow font-normal text-md text-heading">Price Classes</div>}
          <IconButton
            size="md"
            variant="white"
            color="secondaryInteractive"
            onClick={() => {
              hideSidebar();
              setCustomizationTray(null);
              ContextManager.getInstance().setActiveCustomFeatureMenu(null);
            }}
            testId="price-tray-close-btn"
          >
            <Icon.icons.Close />
          </IconButton>
        </Tray.Header>
        <Tray.Tabs currentTab={activeTab} onTabChange={onTabChange}>
          <Tray.Tabs.Tab id="tab-1">Customize</Tray.Tabs.Tab>
          <Tray.Tabs.Content id="tab-1">
            <div className="pt-4 pb-6 flex-grow-0 modal-body overflow-visible">
              <div className="flex gap-1 pb-4">
                <Badge>{defaultPriceClass.currency?.toUpperCase()}</Badge>
                <Badge>{defaultPriceClass.quantityType?.toUpperCase()}</Badge>
              </div>
              <div className="flex-row gap-2">
                <div className="flex gap-1 self-center">
                  <span className="text-heading text-md font-bold">Classes</span>
                  <span className="text-inactive text-md">{`(${priceClasses.length})`}</span>
                </div>
                <span className="text-sm text-lighter text-right">{`${getRoundToLabel()}`}</span>
              </div>

              <MenuSeparatorComponent key={`seperator_main`} />

              {priceClasses?.map((_, index, cp) => {
                return (
                  <React.Fragment key={`fr_${index + cp.length}`}>
                    {renderCustomPriceClasses(index, cp[index].labelError)}
                    {cp.length > 1 ? (
                      <IconButton
                        key={`delete_${index}`}
                        testId={`delete-price-class-btn-${index}`}
                        onClick={() => {
                          deletePriceClass(index);
                        }}
                      >
                        <Icon.icons.Remove />
                      </IconButton>
                    ) : null}
                    <MenuSeparatorComponent key={`seperator_${index}`} />
                  </React.Fragment>
                );
              })}

              <div className=" flex items-center gap-4 ">
                <div className="flex-grow">
                  <div className="flex items-center gap-4">
                    <span>
                      <AddClassButton
                        testId={'add-price-class-btn'}
                        onClick={addPriceClass}
                        isShowing={priceClasses.length < MAX_ALLOWED_CUSTOM_CLASSES}
                      />
                    </span>
                  </div>
                </div>
                <div className="flex">
                  <div className="pl-4">
                    <span>
                      <p className="text-xs text-lighter">lower value</p>
                      <p className="text-sm text-inactive">included</p>
                    </span>
                  </div>
                  <div className="pl-4">
                    <p className="text-xs text-inactive">—</p>
                  </div>
                  <div className="pl-4">
                    <span>
                      <p className="text-xs text-lighter">higher value</p>
                      <p className="text-sm text-inactive">excluded</p>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </Tray.Tabs.Content>
          <Tray.Tabs.Tab id="tab-2">Saved</Tray.Tabs.Tab>
          <Tray.Tabs.Content id="tab-2">
            <div>
              <div>
                {savedPriceClasses
                  .filter(value => isPriceClassApplicable(value, defaultPriceClass))
                  .map(renderSavedPriceClass)}
              </div>
              <div className="border-light border-t py-4 mt-5">
                <div className="font-bold">Unapplicable</div>
                <div className="mb-2 text-sm text-lighter">
                  Price classes that can't be applied with the current currency.
                </div>
                {savedPriceClasses
                  .filter(value => !isPriceClassApplicable(value, defaultPriceClass))
                  .map(renderSavedPriceClass)}
              </div>
            </div>
          </Tray.Tabs.Content>
        </Tray.Tabs>
        {activeTab === FilterTrayTabId.Configure && (
          <Tray.Footer>
            <div className="flex-grow">
              <ButtonGroup>
                <ButtonGroup.PrimaryButton>
                  <Button onClick={onApplyFromCustomizedTab} stretch>
                    Apply
                  </Button>
                </ButtonGroup.PrimaryButton>
                <ButtonGroup.SecondaryButtons>
                  <Button onClick={openSaveModal} mode="secondary" stretch>
                    Save
                  </Button>
                </ButtonGroup.SecondaryButtons>
              </ButtonGroup>
            </div>
          </Tray.Footer>
        )}
      </Tray>

      {renderSavePriceClassModal()}
      {renderSharePriceClassModal()}
    </ClientSidebar.Content>
  );
}
type ComponentProps = {
  sections: FilterSections[];
  updateSettings: (updates: Params) => void;
  currency: string;
  quantityType: string;
};
export { EditTrayComponent };
